import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useItineraryActuals, useDeleteActual, type ItineraryActual } from "@/hooks/useItineraryActuals";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { budgetForItem, sumActualsForItem, toZAR, formatVariancePct } from "@/lib/budgetActualUtils";
import { LogPaymentDialog } from "./budget-actual/LogPaymentDialog";
import { LeadProfitSummary } from "./finance/LeadProfitSummary";

interface Props {
  leadId: string;
  leadCurrency: string;
}

function fmt(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
}

export function BudgetActualTab({ leadId, leadCurrency }: Props) {
  const { data: items, isLoading: itemsLoading } = useQuery({
    queryKey: ["lead-itinerary-items", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("itinerary_items")
        .select("*")
        .eq("lead_id", leadId)
        .or("include_in_quote.is.null,include_in_quote.eq.true")
        .order("date", { ascending: true })
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: invoices } = useQuery({
    queryKey: ["lead-invoices-for-budget", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("amount, amount_received, status")
        .eq("lead_id", leadId);
      if (error) throw error;
      return data || [];
    },
  });

  const { data: actuals, isLoading: actualsLoading } = useItineraryActuals(leadId);
  const { data: rates } = useExchangeRates();
  const deleteActual = useDeleteActual(leadId);

  const [dialogState, setDialogState] = useState<{ open: boolean; itemId: string; itemCurrency: string; supplier?: string | null; existing?: ItineraryActual | null }>({
    open: false, itemId: "", itemCurrency: leadCurrency,
  });

  const grouped = useMemo(() => {
    const map = new Map<string, any[]>();
    (items || []).forEach((it: any) => {
      const key = it.date || "Unscheduled";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(it);
    });
    return Array.from(map.entries());
  }, [items]);

  const totals = useMemo(() => {
    const budgetZar = (items || []).reduce(
      (sum: number, it: any) => sum + toZAR(budgetForItem(it), leadCurrency, rates),
      0,
    );
    const actualZar = (actuals || []).reduce(
      (sum: number, a) => sum + toZAR(Number(a.amount), a.currency, rates),
      0,
    );
    const revenueZar = (invoices || []).reduce((sum: number, inv: any) => {
      const v = inv.amount_received != null ? Number(inv.amount_received) : Number(inv.amount || 0);
      return sum + toZAR(v, leadCurrency, rates);
    }, 0);
    const variance = budgetZar - actualZar;
    const budgetMargin = revenueZar ? ((revenueZar - budgetZar) / revenueZar) * 100 : 0;
    const actualMargin = revenueZar ? ((revenueZar - actualZar) / revenueZar) * 100 : 0;
    return { budgetZar, actualZar, revenueZar, variance, budgetMargin, actualMargin };
  }, [items, actuals, invoices, rates, leadCurrency]);

  if (itemsLoading || actualsLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary strip */}
      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Revenue received" value={fmt(totals.revenueZar, "ZAR")} />
          <Stat label="Budgeted cost" value={fmt(totals.budgetZar, "ZAR")} />
          <Stat label="Actual cost" value={fmt(totals.actualZar, "ZAR")} />
          <Stat
            label="Variance"
            value={fmt(totals.variance, "ZAR")}
            tone={totals.variance >= 0 ? "good" : "bad"}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
          <span>Budget margin <strong className="text-foreground">{totals.budgetMargin.toFixed(1)}%</strong></span>
          <span>Actual margin <strong className="text-foreground">{totals.actualMargin.toFixed(1)}%</strong></span>
          <span>Δ <strong className={totals.actualMargin - totals.budgetMargin >= 0 ? "text-green-600" : "text-red-600"}>
            {(totals.actualMargin - totals.budgetMargin >= 0 ? "+" : "") + (totals.actualMargin - totals.budgetMargin).toFixed(1)}pp
          </strong></span>
        </div>
      </Card>

      <LeadProfitSummary leadId={leadId} />

      {/* Items grouped by day */}
      <div className="space-y-4">
        {grouped.map(([date, dayItems]) => (
          <Card key={date} className="p-4">
            <h3 className="font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wide">
              {date === "Unscheduled" ? "Unscheduled" : format(new Date(date), "EEE, dd MMM yyyy")}
            </h3>
            <div className="space-y-3">
              {dayItems.map((it: any) => {
                const itemActuals = (actuals || []).filter((a) => a.itinerary_item_id === it.id);
                const budget = budgetForItem(it);
                const actualInLeadCcy = sumActualsForItem(itemActuals, leadCurrency, rates);
                const variance = budget - actualInLeadCcy;
                const title = it.custom_title || it.title || "Untitled";
                return (
                  <div key={it.id} className="border rounded-md p-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{title}</span>
                          {it.supplier && <span className="text-xs text-muted-foreground">· {it.supplier}</span>}
                          {it.is_background_cost && <Badge variant="secondary" className="text-xs">Background</Badge>}
                        </div>
                        <div className="mt-1 text-sm flex flex-wrap gap-x-4 gap-y-1">
                          <span className="text-muted-foreground">Budget <strong className="text-foreground">{fmt(budget, leadCurrency)}</strong></span>
                          <span className="text-muted-foreground">Actual <strong className="text-foreground">{fmt(actualInLeadCcy, leadCurrency)}</strong></span>
                          <span className={variance >= 0 ? "text-green-600" : "text-red-600"}>
                            Δ {fmt(variance, leadCurrency)} ({formatVariancePct(budget, actualInLeadCcy)})
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDialogState({ open: true, itemId: it.id, itemCurrency: leadCurrency, supplier: it.supplier, existing: null })}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Log payment
                      </Button>
                    </div>
                    {itemActuals.length > 0 && (
                      <div className="mt-3 pl-3 border-l-2 space-y-1.5">
                        {itemActuals.map((a) => (
                          <div key={a.id} className="flex items-center justify-between text-xs gap-2">
                            <div className="flex-1 min-w-0 truncate">
                              <span className="text-muted-foreground">{format(new Date(a.paid_on), "dd MMM")}</span>
                              <span className="mx-2 font-medium">{fmt(Number(a.amount), a.currency)}</span>
                              {a.reference && <span className="text-muted-foreground">· {a.reference}</span>}
                              {a.notes && <span className="text-muted-foreground"> · {a.notes}</span>}
                            </div>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setDialogState({ open: true, itemId: it.id, itemCurrency: leadCurrency, supplier: it.supplier, existing: a })}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => deleteActual.mutate(a.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      <LogPaymentDialog
        open={dialogState.open}
        onOpenChange={(v) => setDialogState((s) => ({ ...s, open: v }))}
        leadId={leadId}
        itineraryItemId={dialogState.itemId}
        defaultCurrency={dialogState.itemCurrency}
        defaultSupplier={dialogState.supplier}
        existing={dialogState.existing}
      />
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "good" | "bad" }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-semibold ${tone === "good" ? "text-green-600" : tone === "bad" ? "text-red-600" : ""}`}>{value}</div>
    </div>
  );
}